<script lang="ts" setup>
import { graphql } from '@/gql';
import debounce from 'lodash.debounce';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { computed, ref } from 'vue';
import { useRouteParams } from '@vueuse/router';
import {
    VContainer,
    VRow,
    VCol,
    VBtn,
    VCard,
    VSpacer,
    VIcon,
    VList,
    VListItem,
    VListItemTitle,
    VListItemSubtitle,
    VProgressLinear,
    VMenu,
} from 'vuetify/components';
import { RouterLink } from 'vue-router';
import { RouteName } from '@/router';
import { useI18n } from 'vue-i18n';
import { PolicyAction } from '@/types';
import { usePermissions } from '@/composables/permissions';
import ProjectDeleteDialog from './ProjectDeleteDialog.vue';
import SubprojectCreateModal from './SubprojectCreateModal.vue';
import { useUserSettingsStore } from '@/stores/user-settings';
import draggable from 'vuedraggable';
import type { Project } from '@/gql/graphql';
import { useNotificationStore } from '@/stores/notification';

const { hasPermission } = usePermissions();

const { t } = useI18n({
    inheritLocale: true,
    messages: {
        nl: {
            create: '@:subproject aanmaken',
            error: 'Er ging iets mis bij het laden van de @.lower:subprojects',
            no_data: 'Geen @.lower:subprojects beschikbaar',
            order_update_success: 'Subproject volgorde opgeslagen',
            order_update_error: 'Wijzigingen konden niet opgeslagen worden',
            reorder: 'Sorteren',
        },
        en: {
            create: 'Create @.lower:subproject',
            error: 'Failed to load @.lower:subprojects',
            no_data: 'No @.lower:subprojects available',
            order_update_success: 'Saved subproject order',
            order_update_: 'Unable to save subproject order changes',
            reorder: 'Sort',
        },
    },
});

const projectId = useRouteParams('id');
const userSettingsStore = useUserSettingsStore();
const notificationStore = useNotificationStore();

const activePortfolioId = computed(() => userSettingsStore.activePortfolio?.id);

const variables = computed(() => ({
    filters: [
        {
            field: 'id',
            value: projectId.value,
        },
    ],
}));

type SubProject = Pick<
    Project,
    'name' | 'description' | 'id' | 'finished' | 'onHold' | 'position'
>;
const { result, loading, error, onResult } = useQuery(
    graphql(`
        query ProjectSubProjects($filters: [FilterCriterion]) {
            projects(filters: $filters) {
                items {
                    id
                    masterPortfolioId
                    subProjects {
                        name
                        description
                        id
                        finished
                        onHold
                        position
                    }
                }
            }
        }
    `),
    variables,
    {
        fetchPolicy: 'cache-first',
    }
);

const { mutate: mutateSubprojectOrder } = useMutation(
    graphql(`
        mutation UpdateSubprojectOrder($input: UpdateSubProjectOrderInput!) {
            updateSubprojectOrder(input: $input) {
                ok
            }
        }
    `),
    {
        refetchQueries: ['ProjectSubProjects'],
    }
);

const project = computed(() => result.value?.projects?.items[0] || {});
const subProjects = computed(() => project?.value?.subProjects || []);
const totalCount = computed(() => subProjects.value.length);

const draggableItems = ref<SubProject[]>([]);

onResult((value) => {
    if (!value?.data?.projects?.items) return;
    const sorted = [...value.data.projects.items[0].subProjects].sort(
        (a, b) => a.position - b.position
    );
    draggableItems.value = sorted;
});

function projectStatus(finished: string | null, onHold: boolean) {
    if (finished) return 'finished';
    if (onHold) return 'on_hold';
    return 'running';
}

const handleDragEnd = debounce(async () => {
    const inputOrder = draggableItems.value.map((item, index) => ({
        projectId: item.id,
        position: index + 1,
    }));

    try {
        const response = await mutateSubprojectOrder({
            input: {
                order: inputOrder,
                portfolioId: activePortfolioId.value,
                projectId: projectId.value,
            },
        });

        if (response?.data.updateSubprojectOrder.ok) {
            notificationStore.add({
                type: 'success',
                message: t('order_update_success'),
            });
        } else {
            notificationStore.add({
                type: 'error',
                message: t('order_update_error'),
            });
        }
    } catch {
        notificationStore.add({
            type: 'error',
            message: t('order_update_error'),
        });
    }
}, 500);

const isAllowedToDrag = computed(() => {
    return hasPermission(
        PolicyAction.PROJECT_WRITE,
        project.value?.id,
        project.value?.masterPortfolio?.id
    );
});
</script>

<template>
    <v-container class="pt-6">
        <v-row>
            <v-col md="8" class="d-flex align-center">
                <h2 class="text-primary-40 text-title-large">
                    <VIcon
                        icon="$subproject"
                        color="primary-20"
                        size="1.75rem"
                        class="mr-1"
                    />
                    {{ t('subprojects') }}
                </h2>
            </v-col>

            <v-col md="4" class="d-flex justify-end">
                <VSpacer />

                <SubprojectCreateModal
                    v-if="
                        hasPermission(
                            PolicyAction.PROJECT_WRITE,
                            project?.id,
                            project?.masterPortfolio?.id
                        )
                    "
                    :portfolio-id="activePortfolioId || ''"
                    :parent-project-id="String(projectId)"
                >
                    <template #activator="{ props }">
                        <v-btn
                            :disabled="loading"
                            v-bind="props"
                            variant="outlined"
                            prepend-icon="$add"
                        >
                            {{ t('create') }}
                        </v-btn>
                    </template>
                </SubprojectCreateModal>
            </v-col>
        </v-row>
    </v-container>
    <v-container>
        <v-row>
            <v-col md="12">
                <v-card>
                    <v-list
                        v-if="!loading && totalCount > 0"
                        lines="two"
                        bg-color="transparent"
                        class="py-0"
                    >
                        <draggable
                            v-model="draggableItems"
                            item-key="id"
                            animation="200"
                            :disabled="!isAllowedToDrag"
                            @end="handleDragEnd"
                        >
                            <template #item="{ element }">
                                <v-list-item
                                    class="list-item"
                                    prepend-icon="drag_handle"
                                >
                                    <v-list-item-title color="primary-95">
                                        <router-link
                                            variant="text"
                                            :to="{
                                                name: RouteName[
                                                    'dashboard.project'
                                                ],
                                                params: { id: element.id },
                                            }"
                                            class="text-primary text-decoration-none font-weight-bold"
                                        >
                                            {{ element.name }}
                                        </router-link>
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                        {{ element.description }}
                                    </v-list-item-subtitle>
                                    <template #append>
                                        <p
                                            class="text-body-small opacity-20 ml-5"
                                        >
                                            {{
                                                t(
                                                    projectStatus(
                                                        element.finished,
                                                        element.onHold
                                                    )
                                                )
                                            }}
                                        </p>

                                        <v-menu
                                            v-if="
                                                hasPermission(
                                                    PolicyAction.PROJECT_WRITE,
                                                    project?.id,
                                                    project?.masterPortfolio?.id
                                                )
                                            "
                                        >
                                            <template
                                                #activator="{
                                                    props: activatorProps,
                                                }"
                                            >
                                                <v-btn
                                                    icon="$more"
                                                    variant="text"
                                                    class="ml-3"
                                                    :style="{
                                                        '--v-btn-height':
                                                            '2.25rem',
                                                    }"
                                                    v-bind="activatorProps"
                                                ></v-btn>
                                            </template>
                                            <v-list>
                                                <template
                                                    v-if="
                                                        typeof element.id ===
                                                        'string'
                                                    "
                                                >
                                                    <ProjectDeleteDialog
                                                        :project-id="element.id"
                                                    >
                                                        <template
                                                            #activator="{
                                                                props,
                                                            }"
                                                        >
                                                            <v-list-item
                                                                v-bind="props"
                                                                >{{
                                                                    t('delete')
                                                                }}</v-list-item
                                                            >
                                                        </template>
                                                    </ProjectDeleteDialog>
                                                </template>
                                            </v-list>
                                        </v-menu>
                                    </template>
                                </v-list-item>
                            </template>
                        </draggable>
                    </v-list>
                    <p v-if="!loading && totalCount === 0">
                        {{ t('no_data') }}
                    </p>
                    <p v-if="!loading && error">
                        {{ t('error') }}
                    </p>
                    <v-progress-linear v-if="loading" indeterminate />
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<style lang="scss">
.list-item {
    &:hover {
        cursor: grab;

        .v-list-item__prepend {
            color: rgb(var(--v-theme-primary-20));
        }
    }

    &:active {
        cursor: grabbing;
    }
}
</style>
